<template>
  <div class="product-feed-detail-page">
    <div class="container container--fluid">
      <Breadcrumb :items="breadcrumbs" class="product-feed-detail-page__breadcrumb" />
      <PageHeader :title="currentFeed?.name" :description="$t('products.product_feed_description')">
        <template #actions>
          <Btn variant="outlined" icon="upload" @click="openImport">
            {{ $t('products.import.button') }}
          </Btn>
          <Btn @click="addNewProduct">{{ $t('products.add_product') }}</Btn>
        </template>
      </PageHeader>

      <!-- Products Table -->
      <div class="products-section">
        <div class="section-header">
          <h2>Producten</h2>
          <div class="section-actions">
            <div class="view-toggle">
              <button
                class="view-toggle__pill"
                :class="{ 'view-toggle__pill--active': viewMode === 'grid' }"
                @click="viewMode = 'grid'"
              >
                <BaseIcon name="cubes" size="14" />
                Cards
              </button>
              <button
                class="view-toggle__pill"
                :class="{ 'view-toggle__pill--active': viewMode === 'list' }"
                @click="viewMode = 'list'"
              >
                <BaseIcon name="list-ul" size="14" />
                Lijst
              </button>
            </div>
            <input v-model="searchQuery" placeholder="Zoek producten..." class="search-input" />
            <select v-model="selectedCategory" class="category-filter">
              <option value="">Alle categorieën</option>
              <option v-for="category in uniqueCategories" :key="category" :value="category">
                {{ category }}
              </option>
            </select>
          </div>
        </div>

        <EmptyState
          v-if="!filteredProducts.length"
          :title="$t('products.import.empty_title')"
          :description="$t('products.import.empty_description')"
          icon="package"
        >
          <div class="empty-state__actions">
            <Btn variant="outlined" icon="upload" @click="openImport">
              {{ $t('products.import.button') }}
            </Btn>
            <Btn variant="primary" @click="addNewProduct">
              {{ $t('products.add_product') }}
            </Btn>
          </div>
        </EmptyState>

        <!-- List View -->
        <div v-else-if="viewMode === 'list'" class="products-table">
          <EasyDataTable
            :headers="productHeaders"
            :items="filteredProducts"
            :rows-per-page="25"
            table-class-name="custom-table"
            @click-row="editProduct"
          >
            <template #item-thumbnail="item">
              <div class="products-table__thumbnail">
                <img
                  v-if="item.imageUrl"
                  :src="item.imageUrl"
                  :alt="item.title"
                  @error="handleImageError"
                />
                <div v-else class="products-table__thumbnail-placeholder">
                  <BaseIcon name="image" size="16" />
                </div>
              </div>
            </template>
            <template #item-title="item">
              <span class="products-table__title">{{ item.title }}</span>
              <Label
                v-if="item.catalogInstallationId"
                variant="info"
                size="sm"
                class="products-table__catalog-label"
              >
                Catalogus
              </Label>
            </template>
            <template #item-price="item">
              <span v-if="item.salePrice" class="products-table__sale-price">
                €{{ item.salePrice }}
              </span>
              <span v-if="item.price" :class="{ 'products-table__original-price': item.salePrice }">
                €{{ item.price }}
              </span>
              <span v-else-if="!item.salePrice">-</span>
            </template>
            <template #item-categories="item">
              <div v-if="item.categories?.length" class="product-categories">
                <span
                  v-for="category in item.categories.slice(0, 2)"
                  :key="category.id"
                  class="category-tag"
                  :style="{ backgroundColor: category.color }"
                >
                  {{ category.name }}
                </span>
                <span v-if="item.categories.length > 2" class="more-categories">
                  +{{ item.categories.length - 2 }}
                </span>
              </div>
              <span v-else>-</span>
            </template>
            <template #item-updatedAt="item">
              {{ getProductUpdatedAt(item) ? formatDate(getProductUpdatedAt(item)) : '-' }}
            </template>
            <template #item-actions="item">
              <div class="products-table__actions">
                <button @click.stop="editProduct(item)" title="Bewerken">
                  <BaseIcon name="edit" size="16" />
                </button>
                <button @click.stop="bulkUpdateProductPrice(item)" title="Prijsupdate">
                  <BaseIcon name="zap" size="16" />
                </button>
                <button @click.stop="duplicateProduct(item)" title="Dupliceren">
                  <BaseIcon name="clone" size="16" />
                </button>
                <button @click.stop="moveProduct(item)" title="Verplaatsen naar andere feed">
                  <BaseIcon name="arrow-right" size="16" />
                </button>
                <button
                  class="products-table__action--danger"
                  @click.stop="deleteProduct(item)"
                  title="Verwijderen"
                >
                  <BaseIcon name="trash" size="16" />
                </button>
              </div>
            </template>
          </EasyDataTable>
        </div>

        <!-- Card View -->
        <div v-else>
          <div class="products-grid">
            <div
              v-for="(product, index) in displayedProducts"
              :key="getProductKey(product, index)"
              class="product-card"
              @click="editProduct(product, index)"
            >
              <div class="product-image">
                <img
                  v-if="product.imageUrl"
                  :src="product.imageUrl"
                  :alt="product.title"
                  @error="handleImageError"
                />
                <div v-else class="placeholder-image">
                  <BaseIcon name="image" size="24" />
                </div>
              </div>

              <div class="product-info">
                <h3>{{ product.title }}</h3>
                <div
                  v-if="product.catalogInstallationId || product.catalogLinkStatus === 'partial'"
                  class="product-catalog-status"
                >
                  <Label v-if="product.catalogInstallationId" variant="info" size="sm">
                    Catalogus
                  </Label>
                  <Label v-if="product.catalogLinkStatus === 'partial'" variant="warning" size="sm">
                    Deels losgekoppeld
                  </Label>
                </div>
                <p v-if="getProductUpdatedAt(product)" class="product-updated-at">
                  Laatst gewijzigd: {{ formatDate(getProductUpdatedAt(product)) }}
                </p>
                <p v-if="product.description" class="product-description">
                  {{ product.description }}
                </p>

                <div class="product-meta">
                  <div v-if="product.price" class="product-price">
                    <span v-if="product.salePrice" class="sale-price">
                      €{{ product.salePrice }}
                    </span>
                    <span :class="{ 'original-price': product.salePrice }">
                      €{{ product.price }}
                    </span>
                  </div>

                  <div v-if="product.categories?.length" class="product-categories">
                    <span
                      v-for="category in product.categories.slice(0, 2)"
                      :key="category.id"
                      class="category-tag"
                      :style="{ backgroundColor: category.color }"
                    >
                      {{ category.name }}
                    </span>
                    <span v-if="product.categories.length > 2" class="more-categories">
                      +{{ product.categories.length - 2 }}
                    </span>
                  </div>
                </div>

                <div class="product-actions">
                  <Btn
                    variant="outlined"
                    size="sm"
                    @click.stop="editProduct(product, index)"
                    icon="edit"
                  >
                    Bewerken
                  </Btn>
                  <Btn
                    variant="outlined"
                    size="sm"
                    @click.stop="bulkUpdateProductPrice(product)"
                    icon="zap"
                    type="button"
                  >
                    Prijsupdate
                  </Btn>
                  <Btn
                    variant="outlined"
                    size="sm"
                    @click.stop="duplicateProduct(product)"
                    icon="clone"
                  >
                    Dupliceren
                  </Btn>
                  <Btn
                    variant="outlined"
                    size="sm"
                    @click.stop="moveProduct(product)"
                    icon="arrow-right"
                    type="button"
                  >
                    Verplaatsen
                  </Btn>
                  <Btn
                    variant="danger"
                    size="sm"
                    @click.stop="deleteProduct(product)"
                    icon="trash"
                    type="button"
                  >
                    Verwijderen
                  </Btn>
                </div>
              </div>
            </div>
          </div>

          <!-- Load More Button -->
          <div v-if="hasMoreProducts" class="load-more-container">
            <Btn variant="secondary" @click="loadMore" :loading="loadingMore">
              {{ $t('products.load_more') }} ({{ remainingProductsCount }}
              {{ $t('products.remaining') }})
            </Btn>
          </div>

          <!-- Infinite Scroll Observer -->
          <div ref="scrollObserver" class="scroll-observer"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    defineComponent,
    computed,
    ref,
    onMounted,
    onUnmounted,
    watch,
    getCurrentInstance,
  } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';
  import { formatDate } from '@/utils/date';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import {
    getProductFeedSummaries,
    deleteProduct as deleteProductApi,
    duplicateProduct as duplicateProductApi,
    moveProductToFeed,
    bulkUpdateProducts,
  } from '@shared/services/productService';

  import Breadcrumb from '@/components/partials/Breadcrumb.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import Label from '@/components/partials/Label.vue';
  import BulkPriceUpdateModal from '@/components/products/BulkPriceUpdateModal.vue';
  import MoveProductModal from '@/components/products/MoveProductModal.vue';
  import ProductImportModal from '@/components/products/ProductImportModal.vue';

  export default defineComponent({
    name: 'ProductFeedDetailPage',

    components: {
      Breadcrumb,
      Btn,
      BaseIcon,
      Label,
      PageHeader,
      EmptyState,
      EasyDataTable,
    },

    setup() {
      const router = useRouter();
      const route = useRoute();
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const feedId = route.params.feedId;
      const feeds = ref([]);
      const currentFeed = ref(null);
      const searchQuery = ref('');
      const selectedCategory = ref('');
      const viewMode = ref(localStorage.getItem('productFeedViewMode') || 'grid');

      const productHeaders = [
        { text: '', value: 'thumbnail', sortable: false, width: 60 },
        { text: 'Titel', value: 'title', sortable: true },
        { text: 'Prijs', value: 'price', sortable: true },
        { text: 'Categorieën', value: 'categories', sortable: false },
        { text: 'Laatst gewijzigd', value: 'updatedAt', sortable: true },
        { text: 'Acties', value: 'actions', sortable: false, width: 160 },
      ];

      watch(viewMode, (mode) => {
        localStorage.setItem('productFeedViewMode', mode);
      });
      const itemsPerPage = ref(12);
      const displayedCount = ref(12);
      const loadingMore = ref(false);
      const scrollObserver = ref(null);
      let intersectionObserver = null;

      const breadcrumbs = computed(() => [
        {
          label: 'Product feeds',
          url: '/products',
          icon: 'box',
        },
        {
          label: currentFeed.value?.name || 'Feed',
        },
      ]);

      const uniqueCategories = computed(() => {
        if (!currentFeed.value?.products) return [];

        const categories = new Set();
        currentFeed.value.products.forEach((product) => {
          if (product.categories) {
            product.categories.forEach((cat) => categories.add(cat.name));
          }
        });

        return Array.from(categories).sort();
      });

      const filteredProducts = computed(() => {
        if (!currentFeed.value?.products) return [];

        let products = currentFeed.value.products;

        // Filter by search query
        if (searchQuery.value) {
          const query = searchQuery.value.toLowerCase();
          products = products.filter(
            (product) =>
              product.title.toLowerCase().includes(query) ||
              product.description?.toLowerCase().includes(query)
          );
        }

        // Filter by category
        if (selectedCategory.value) {
          products = products.filter((product) =>
            product.categories?.some((cat) => cat.name === selectedCategory.value)
          );
        }

        return products;
      });

      const displayedProducts = computed(() => {
        return filteredProducts.value.slice(0, displayedCount.value);
      });

      const hasMoreProducts = computed(() => {
        return displayedCount.value < filteredProducts.value.length;
      });

      const remainingProductsCount = computed(() => {
        return filteredProducts.value.length - displayedCount.value;
      });

      const getProductKey = (product, index) => {
        return product.id || `product-${index}`;
      };

      const getProductUpdatedAt = (product) => {
        return product.updated_at || product.updatedAt || null;
      };

      // Load data
      onMounted(async () => {
        try {
          const organizationId = userStore.currentOrganizationId;
          if (!organizationId) {
            console.error('No organization ID available');
            return;
          }

          feeds.value = await getProductFeedSummaries();
          console.log(
            'Looking for feedId:',
            feedId,
            'in feeds:',
            feeds.value.map((f) => ({ id: f.id, name: f.name }))
          );
          currentFeed.value = feeds.value.find((f) => f.id === feedId);

          // Als geen feed gevonden, probeer fallback naar mock data zonder organizationId
          if (!currentFeed.value && feeds.value.length === 0) {
            console.log('No feeds found with organizationId, trying fallback to mock data...');
            feeds.value = await getProductFeedSummaries();
            currentFeed.value = feeds.value.find((f) => f.id === feedId);
          }

          if (!currentFeed.value) {
            console.error(
              'Feed not found. Available feeds:',
              feeds.value.map((f) => f.id)
            );
            router.push('/products');
          }
        } catch (error) {
          console.error('Error loading feed data:', error);
        }
      });

      onUnmounted(() => {
        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }
      });

      // Methods
      const goBack = () => {
        router.push('/products');
      };

      const addNewProduct = () => {
        router.push(`/products/${feedId}/new`);
      };

      const refreshFeed = async () => {
        const organizationId = userStore.currentOrganizationId;
        feeds.value = await getProductFeedSummaries();
        currentFeed.value = feeds.value.find((f) => f.id === feedId);
      };

      const openImport = () => {
        uiStore.addModal(ProductImportModal, {
          title: $t('products.import.title'),
          size: 'xl',
          feedId,
          onImported: async (result) => {
            await refreshFeed();
            toastStore.addToast({
              type: 'success',
              message: $t('products.import.success', {
                created: result?.created_count || 0,
                updated: result?.updated_count || 0,
              }),
            });
          },
        });
      };

      const editProduct = (product, productIndex) => {
        // If product object is passed, use it; otherwise fallback to index lookup
        const productToEdit = product || displayedProducts.value[productIndex];
        if (productToEdit?.id) {
          // Use product UUID if available
          router.push(`/products/${feedId}/${productToEdit.id}`);
        } else {
          // Find the product in filteredProducts to get the correct index
          const indexInFiltered = filteredProducts.value.findIndex(
            (p) =>
              p === productToEdit ||
              (p.title === productToEdit?.title && p.description === productToEdit?.description)
          );
          if (indexInFiltered !== -1) {
            router.push(`/products/${feedId}/${indexInFiltered}`);
          } else {
            // Fallback to index for mock data
            router.push(`/products/${feedId}/${productIndex}`);
          }
        }
      };

      const deleteProduct = async (product) => {
        if (!product?.id) {
          toastStore.addToast({
            type: 'error',
            message: 'Dit product kan niet verwijderd worden omdat er geen ID beschikbaar is',
          });
          return;
        }

        const modalId = uiStore.addModal(false, {
          title: 'Product verwijderen?',
          description: `Weet je zeker dat je "${product.title}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`,
          submitText: 'Ja, verwijderen',
          cancelText: 'Annuleren',
          status: 'danger',
          onSubmit: async () => {
            try {
              await deleteProductApi(product.id);
              uiStore.closeModal(modalId);
              toastStore.addToast({
                type: 'success',
                message: 'Product verwijderd',
              });

              const organizationId = userStore.currentOrganizationId;
              feeds.value = await getProductFeedSummaries();
              currentFeed.value = feeds.value.find((f) => f.id === feedId);
            } catch (error) {
              console.error('Error deleting product:', error);
              toastStore.addToast({
                type: 'error',
                message: 'Er is een fout opgetreden bij het verwijderen van het product',
              });
            }
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const duplicateProduct = (product) => {
        if (!product?.id) return;

        const modalId = uiStore.addModal(false, {
          title: 'Product dupliceren',
          description: `Weet je zeker dat je "${product.title}" wilt dupliceren?`,
          submitText: 'Dupliceren',
          cancelText: 'Annuleren',
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            try {
              await duplicateProductApi(product.id);
              toastStore.addToast({ type: 'success', message: 'Product gedupliceerd' });
              const organizationId = userStore.currentOrganizationId;
              feeds.value = await getProductFeedSummaries();
              currentFeed.value = feeds.value.find((f) => f.id === feedId);
            } catch (error) {
              toastStore.addToast({ type: 'error', message: 'Fout bij dupliceren van product' });
            }
          },
          onCancel: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const moveProduct = (product) => {
        if (!product?.id) {
          toastStore.addToast({
            type: 'error',
            message: 'Dit product kan niet verplaatst worden omdat er geen ID beschikbaar is',
          });
          return;
        }

        if (feeds.value.filter((f) => f.id !== feedId).length === 0) {
          toastStore.addToast({
            type: 'info',
            message: 'Er zijn geen andere feeds om dit product naartoe te verplaatsen',
          });
          return;
        }

        let isMoving = false;
        const modalId = uiStore.addModal(MoveProductModal, {
          title: 'Product verplaatsen',
          size: 'sm',
          description: `Naar welke feed wil je "${product.title}" verplaatsen?`,
          feeds: feeds.value,
          currentFeedId: feedId,
          beforeClose: () => !isMoving,
          onConfirm: async (targetFeedId) => {
            isMoving = true;
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              await moveProductToFeed(product.id, targetFeedId);
              isMoving = false;
              uiStore.closeModal(modalId);

              const targetFeed = feeds.value.find((f) => f.id === targetFeedId);
              toastStore.addToast({
                type: 'success',
                message: `Product verplaatst naar "${targetFeed?.name || 'andere feed'}"`,
              });

              const organizationId = userStore.currentOrganizationId;
              feeds.value = await getProductFeedSummaries();
              currentFeed.value = feeds.value.find((f) => f.id === feedId);
            } catch (error) {
              console.error('Error moving product:', error);
              isMoving = false;
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                type: 'error',
                message: 'Er is een fout opgetreden bij het verplaatsen van het product',
              });
            }
          },
        });
      };

      const bulkUpdateProductPrice = async (product) => {
        if (!product?.id) {
          toastStore.addToast({
            type: 'error',
            message: 'Dit product kan niet aangepast worden omdat er geen ID beschikbaar is',
          });
          return;
        }

        let isUpdating = false;
        const modalId = uiStore.addModal(BulkPriceUpdateModal, {
          title: 'Prijsupdate',
          size: 'sm',
          description: `Met hoeveel procent wil je de prijs van "${product.title}" aanpassen?`,
          defaultPercentage: 5,
          currentPrice: Number.isFinite(Number(product.price)) ? Number(product.price) : null,
          beforeClose: () => !isUpdating,
          onConfirm: async (percentage) => {
            const payload = {
              mode: 'percentage',
              field: 'price',
              value: percentage,
              filter: { product_ids: [product.id] },
            };

            isUpdating = true;
            uiStore.updateModalProps(modalId, { loading: true });
            try {
              const previewResult = await bulkUpdateProducts(payload);
              const fieldsCount =
                previewResult.scaled_pricing_fields ??
                previewResult.preview?.[0]?.scaled_pricing_fields ??
                0;

              isUpdating = false;
              uiStore.closeModal(modalId);

              const confirmModalId = uiStore.addModal(false, {
                title: 'Prijsupdate uitvoeren?',
                description:
                  `Er worden ${fieldsCount} prijsvelden bijgewerkt. Weet je het zeker? ` +
                  'Dit kan niet ongedaan worden gemaakt.',
                submitText: 'Ja, prijsupdate uitvoeren',
                cancelText: 'Annuleren',
                status: 'danger',
                onSubmit: async () => {
                  uiStore.updateModalProps(confirmModalId, { loading: true, closable: false });
                  try {
                    await bulkUpdateProducts({
                      ...payload,
                      dry_run: false,
                    });
                    uiStore.closeModal(confirmModalId);
                    toastStore.addToast({ type: 'success', message: 'Productprijs bijgewerkt' });
                    const organizationId = userStore.currentOrganizationId;
                    feeds.value = await getProductFeedSummaries();
                    currentFeed.value = feeds.value.find((f) => f.id === feedId);
                  } catch (error) {
                    console.error('Error bulk updating product:', error);
                    uiStore.updateModalProps(confirmModalId, {
                      loading: false,
                      closable: true,
                    });
                    toastStore.addToast({
                      type: 'error',
                      message: 'Er is een fout opgetreden bij de prijsupdate',
                    });
                  }
                },
                onCancel: () => {
                  uiStore.closeModal(confirmModalId);
                },
              });
            } catch (error) {
              console.error('Error previewing product price update:', error);
              uiStore.updateModalProps(modalId, { loading: false });
              toastStore.addToast({
                type: 'error',
                message: 'Er is een fout opgetreden bij de prijsupdate-preview',
              });
              isUpdating = false;
            }
          },
        });
      };

      const handleImageError = (event) => {
        event.target.style.display = 'none';
      };

      const loadMore = () => {
        if (loadingMore.value || !hasMoreProducts.value) return;

        loadingMore.value = true;
        // Simulate slight delay for better UX
        setTimeout(() => {
          displayedCount.value = Math.min(
            displayedCount.value + itemsPerPage.value,
            filteredProducts.value.length
          );
          loadingMore.value = false;
        }, 200);
      };

      const setupIntersectionObserver = () => {
        if (!scrollObserver.value || typeof IntersectionObserver === 'undefined') {
          return;
        }

        intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && hasMoreProducts.value && !loadingMore.value) {
                loadMore();
              }
            });
          },
          {
            rootMargin: '200px', // Start loading 200px before reaching the bottom
          }
        );

        intersectionObserver.observe(scrollObserver.value);
      };

      // Reset displayed count when filters change
      watch([searchQuery, selectedCategory], () => {
        displayedCount.value = itemsPerPage.value;
      });

      // Setup intersection observer when component is mounted and products are loaded
      watch([filteredProducts, scrollObserver], () => {
        if (filteredProducts.value.length > 0 && scrollObserver.value) {
          // Small delay to ensure DOM is ready
          setTimeout(() => {
            setupIntersectionObserver();
          }, 100);
        }
      });

      return {
        breadcrumbs,
        currentFeed,
        searchQuery,
        selectedCategory,
        viewMode,
        productHeaders,
        uniqueCategories,
        filteredProducts,
        displayedProducts,
        hasMoreProducts,
        remainingProductsCount,
        loadingMore,
        scrollObserver,
        goBack,
        addNewProduct,
        openImport,
        editProduct,
        deleteProduct,
        duplicateProduct,
        moveProduct,
        bulkUpdateProductPrice,
        handleImageError,
        loadMore,
        getProductKey,
        getProductUpdatedAt,
        formatDate,
      };
    },
  });
</script>

<style scoped lang="scss">
  .product-feed-detail-page {
    min-height: 100vh;
    background: var(--slate-50);

    &__breadcrumb {
      margin: 20px 0 1rem 0;
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 2rem;
      padding: 1.5rem;
      background: white;
      border-radius: 8px;
      border: 1px solid var(--slate-200);
    }

    &__title h1 {
      margin: 0 0 0.5rem 0;
      font-size: 1.875rem;
      font-weight: 700;
      color: var(--slate-900);
    }

    &__subtitle {
      margin: 0;
      color: var(--slate-600);
    }

    &__actions {
      display: flex;
      gap: 0.75rem;
    }

    &__content {
      max-width: 1400px;
    }
  }

  .feed-info-card {
    background: white;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .feed-stats {
    display: flex;
    gap: 2rem;
    margin-bottom: 1rem;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;

    .stat-number {
      font-size: 2rem;
      font-weight: 700;
      color: var(--blue-600);
    }

    .stat-label {
      font-size: 0.875rem;
      color: var(--slate-600);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }

  .feed-description {
    padding-top: 1rem;
    border-top: 1px solid var(--slate-200);
    color: var(--slate-700);
  }

  .products-section {
    background: white;
    border-radius: 8px;
    border: 1px solid var(--slate-200);
    padding: 1.5rem;
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;

    h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--slate-900);
    }
  }

  .section-actions {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .view-toggle {
    display: inline-flex;
    align-items: center;
    background: var(--slate-100);
    border-radius: 0.5rem;
    padding: 0.25rem;

    &__pill {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.875rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-500);
      background: transparent;
      border: 1px solid transparent;
      border-radius: 0.375rem;
      cursor: pointer;
      transition:
        color 0.15s ease,
        background-color 0.15s ease,
        border-color 0.15s ease;

      &:hover {
        color: var(--slate-700);
      }

      &--active {
        background: white;
        border-color: var(--slate-200);
        color: var(--slate-900);
        box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
      }
    }
  }

  .products-table {
    margin: 0 -1.5rem -1.5rem;

    &__thumbnail {
      width: 48px;
      height: 48px;
      border-radius: 6px;
      overflow: hidden;
      background: var(--slate-100);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0.375rem 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__thumbnail-placeholder {
      color: var(--slate-400);
      display: flex;
    }

    &__title {
      font-weight: 500;
      color: var(--slate-900);
    }

    &__catalog-label {
      margin-left: 0.5rem;
    }

    &__sale-price {
      font-weight: 600;
      color: var(--red-600);
      margin-right: 0.5rem;
    }

    &__original-price {
      text-decoration: line-through;
      color: var(--slate-500);
    }

    &__actions {
      display: flex;
      gap: 0.25rem;

      button {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0.375rem;
        background: transparent;
        border: none;
        border-radius: 4px;
        color: var(--slate-500);
        cursor: pointer;
        transition: all 0.15s ease;

        @include hover {
          background: var(--slate-100);
          color: var(--slate-700);
        }
      }

      .products-table__action--danger {
        @include hover {
          background: var(--red-50);
          color: var(--red-600);
        }
      }
    }
  }

  .search-input,
  .category-filter {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--slate-300);
    border-radius: 6px;
    font-size: 0.875rem;

    &:focus {
      outline: none;
      border-color: var(--blue-500);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }

  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: var(--slate-500);

    h3 {
      margin: 1rem 0 0.5rem 0;
      color: var(--slate-700);
    }

    p {
      margin: 0 0 2rem 0;
    }

    &__actions {
      display: flex;
      justify-content: center;
      gap: 0.75rem;
      flex-wrap: wrap;
    }
  }

  .products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  .product-card {
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: var(--blue-300);
    }
  }

  .product-image {
    height: 200px;
    background: var(--slate-100);
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .placeholder-image {
    color: var(--slate-400);
  }

  .product-info {
    padding: 1rem;

    h3 {
      margin: 0 0 0.5rem 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-900);
    }
  }

  .product-catalog-status {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    margin: 0 0 0.35rem;
  }

  .product-updated-at {
    margin: 0 0 0.5rem 0;
    font-size: 0.75rem;
    color: var(--slate-500);
  }

  .product-description {
    margin: 0 0 1rem 0;
    font-size: 0.875rem;
    color: var(--slate-600);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .product-meta {
    margin-bottom: 1rem;
  }

  .product-price {
    margin-bottom: 0.5rem;

    .sale-price {
      font-weight: 600;
      color: var(--red-600);
      margin-right: 0.5rem;
    }

    .original-price {
      text-decoration: line-through;
      color: var(--slate-500);
    }
  }

  .product-categories {
    display: flex;
    gap: 0.25rem;
    flex-wrap: wrap;
  }

  .category-tag {
    padding: 0.25rem 0.5rem;
    @include fontSize('sm');
    font-weight: 500;
    color: white;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.875rem;
  }

  .more-categories {
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    background: var(--slate-200);
    color: var(--slate-700);
  }

  .product-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .load-more-container {
    display: flex;
    justify-content: center;
    padding: 2rem 0;
    margin-top: 1.5rem;
  }

  .scroll-observer {
    height: 1px;
    width: 100%;
    margin-top: 1rem;
  }
</style>
